import type { Column, Operators, SQL, SQLWrapper } from 'drizzle-orm';
import type { FormatQueryFinalOptions, RuleGroupType, RuleProcessor } from '../../types';
import { toArray } from '../arrayUtils';
import { lc } from '../misc';
import { parseNumber } from '../parseNumber';
import { transformQuery } from '../transformQuery';
import { defaultRuleGroupProcessorDrizzle } from './defaultRuleGroupProcessorDrizzle';
import {
  getSubqueryElementAlias,
  isValidValue,
  processMatchMode,
  shouldRenderAsNumber,
} from './utils';

/**
 * Default rule processor used by {@link formatQuery} for the "drizzle" format.
 *
 * @group Export
 */
export const defaultRuleProcessorDrizzle: RuleProcessor = (rule, _options): SQL | undefined => {
  const opts = _options ?? /* v8 ignore start -- @preserve */ {}; /* v8 ignore stop -- @preserve */
  // v8 ignore next
  const { parseNumbers, preserveValueOrder, context = {} } = opts;
  const { columns, drizzleOperators, useRawFields } = context as {
    columns: Record<string, Column>;
    drizzleOperators: Operators;
    useRawFields?: boolean;
  };

  if (!columns || !drizzleOperators) return undefined;

  const {
    between,
    eq,
    gt,
    gte,
    inArray,
    isNotNull,
    isNull,
    like,
    lt,
    lte,
    ne,
    notBetween,
    notInArray,
    notLike,
    sql,
  } = drizzleOperators;

  const { field, operator, value, valueSource } = rule;
  const column =
    useRawFields && /^[a-z_][a-z0-9_.]*$/i.test(field)
      ? (sql.raw(field) as Exclude<SQLWrapper, SQL.Aliased | Column>)
      : columns[field];
  const operatorLC = lc(operator);

  const valueIsField = valueSource === 'field';
  const asFieldOrValue = (v: string | number) => (valueIsField ? columns[v] : v);

  // Parse value as number when applicable
  const maybeParseNumber = (v: unknown) => {
    if (valueIsField || !parseNumbers) return asFieldOrValue(v as string);
    return shouldRenderAsNumber(v, true) ? parseNumber(v, { parseNumbers: true }) : v;
  };

  if (!column) return undefined;

  const matchEval = processMatchMode(rule);

  if (matchEval === false) {
    return undefined;
  } else if (matchEval) {
    // We only support PostgreSQL nested arrays
    if (opts.preset !== 'postgresql') return undefined;

    const { mode, threshold } = matchEval;

    const subqueryDepth = opts.subqueryDepth ?? 0;
    const arrayElementAlias = getSubqueryElementAlias(subqueryDepth);

    const sqlQuery = transformQuery(rule.value as RuleGroupType, {
      ruleProcessor: r => ({ ...r, field: arrayElementAlias }),
    });

    const nestedArrayFilter = defaultRuleGroupProcessorDrizzle(sqlQuery, {
      ...(opts as FormatQueryFinalOptions),
      context: { ...opts.context, useRawFields: true },
      subqueryDepth: subqueryDepth + 1,
    });

    switch (mode) {
      case 'all':
        return sql`(select count(*) from unnest(${column}) as ${sql.raw(arrayElementAlias)} where ${nestedArrayFilter({}, drizzleOperators)}) = array_length(${column}, 1)`;

      case 'none':
        return sql`not exists (select 1 from unnest(${column}) as ${sql.raw(arrayElementAlias)} where ${nestedArrayFilter({}, drizzleOperators)})`;

      case 'some':
        return sql`exists (select 1 from unnest(${column}) as ${sql.raw(arrayElementAlias)} where ${nestedArrayFilter({}, drizzleOperators)})`;

      case 'atleast':
      case 'atmost':
      case 'exactly': {
        const op = mode === 'atleast' ? '>=' : mode === 'atmost' ? '<=' : '=';

        return threshold > 0 && threshold < 1
          ? sql`(select count(*)::float / array_length(${column}, 1) from unnest(${column}) as ${sql.raw(arrayElementAlias)} where ${nestedArrayFilter({}, drizzleOperators)}) ${sql.raw(`${op} ${threshold}`)}`
          : sql`(select count(*) from unnest(${column}) as ${sql.raw(arrayElementAlias)} where ${nestedArrayFilter({}, drizzleOperators)}) ${sql.raw(`${op} ${threshold}`)}`;
      }
    }
  }

  switch (operatorLC) {
    case '=':
      return eq(column, maybeParseNumber(value));
    case '!=':
      return ne(column, maybeParseNumber(value));
    case '>':
      return gt(column, maybeParseNumber(value));
    case '<':
      return lt(column, maybeParseNumber(value));
    case '>=':
      return gte(column, maybeParseNumber(value));
    case '<=':
      return lte(column, maybeParseNumber(value));
    case 'beginswith':
    case 'doesnotbeginwith':
      return (operatorLC === 'doesnotbeginwith' ? notLike : like)(
        column as SQL,
        valueIsField ? sql`${asFieldOrValue(value)} || '%'` : `${value}%`
      );
    case 'contains':
    case 'doesnotcontain':
      return (operatorLC === 'doesnotcontain' ? notLike : like)(
        column as SQL,
        valueIsField ? sql`'%' || ${asFieldOrValue(value)} || '%'` : `%${value}%`
      );
    case 'endswith':
    case 'doesnotendwith':
      return (operatorLC === 'doesnotendwith' ? notLike : like)(
        column as SQL,
        valueIsField ? sql`'%' || ${asFieldOrValue(value)}` : `%${value}`
      );
    case 'null':
      return isNull(column);
    case 'notnull':
      return isNotNull(column);
    case 'in':
    case 'notin': {
      const valueAsArray = toArray(value).map(v => maybeParseNumber(v));
      return operatorLC === 'notin'
        ? notInArray(column, valueAsArray)
        : inArray(column, valueAsArray);
    }
    case 'between':
    case 'notbetween': {
      const valueAsArray = toArray(value);
      if (
        valueAsArray.length >= 2 &&
        isValidValue(valueAsArray[0]) &&
        isValidValue(valueAsArray[1])
      ) {
        type ColumnType = (typeof columns)[keyof typeof columns];
        type ValueAsArray = (string | number | ColumnType)[];
        let [first, second]: ValueAsArray = valueAsArray;
        // For backwards compatibility, default to parsing numbers for between operators
        // unless parseNumbers is explicitly set to false
        const shouldParseNumbers = !(parseNumbers === false);
        if (
          !valueIsField &&
          shouldRenderAsNumber(first, shouldParseNumbers) &&
          shouldRenderAsNumber(second, shouldParseNumbers)
        ) {
          const firstNum = parseNumber(first, { parseNumbers: shouldParseNumbers });
          const secondNum = parseNumber(second, { parseNumbers: shouldParseNumbers });
          if (!preserveValueOrder && secondNum < firstNum) {
            const tempNum = secondNum;
            second = firstNum;
            first = tempNum;
          } else {
            first = firstNum;
            second = secondNum;
          }
        } else {
          // v8 ignore else
          if (valueIsField) {
            first = asFieldOrValue(first);
            second = asFieldOrValue(second);
          }
        }
        return operatorLC === 'notbetween'
          ? notBetween(column, first, second)
          : between(column, first, second);
      }
      return undefined;
    }
    default:
      return undefined;
  }
};
